/**
 * Service Health Ticker Types
 *
 * Data types for the vendor service health ticker that displays
 * real-time status from Microsoft 365, Azure, ConnectWise, Datto, and ITGlue.
 */

export type VendorId = 'microsoft365' | 'azure' | 'connectwise' | 'datto' | 'itglue';
export type Severity = 'operational' | 'maintenance' | 'minor' | 'major' | 'critical';

export interface TickerItem {
  id: string;
  vendor: VendorId;
  vendorName: string;
  vendorIcon: string;
  title: string;
  severity: Severity;
  status: string;
  startedAt: string;
  updatedAt: string;
  affectedServices: string[];
  affectedClients?: string[];  // For Microsoft: client names affected via GDAP
  updates?: IncidentUpdate[];
  statusPageUrl?: string;
}

export interface IncidentUpdate {
  timestamp: string;
  status: string;
  message: string;
}

export interface VendorStatus {
  fetched: boolean;
  count: number;
  message?: string;
}

export interface ServiceHealthResponse {
  items: TickerItem[];
  lastUpdated: string;
  vendors: Record<VendorId, VendorStatus>;
}

// Vendor display configuration
export const vendorConfig: Record<VendorId, { name: string; icon: string; color: string }> = {
  microsoft365: { name: 'Microsoft 365', icon: 'M365', color: '#0078d4' },
  azure: { name: 'Azure', icon: 'AZ', color: '#0089d6' },
  connectwise: { name: 'ConnectWise', icon: 'CW', color: '#e31937' },
  datto: { name: 'Datto', icon: 'DT', color: '#199ed9' },
  itglue: { name: 'ITGlue', icon: 'IG', color: '#1a73e8' }
};

// Severity display configuration
export const severityConfig: Record<Severity, { label: string; color: string; bgColor: string }> = {
  operational: { label: 'OK', color: 'var(--accent-green)', bgColor: 'rgba(34, 197, 94, 0.2)' },
  maintenance: { label: 'MAINT', color: 'var(--accent-purple)', bgColor: 'rgba(168, 85, 247, 0.2)' },
  minor: { label: 'MINOR', color: 'var(--accent-yellow)', bgColor: 'rgba(234, 179, 8, 0.2)' },
  major: { label: 'MAJOR', color: 'var(--accent-orange)', bgColor: 'rgba(249, 115, 22, 0.2)' },
  critical: { label: 'CRIT', color: 'var(--accent-red)', bgColor: 'rgba(239, 68, 68, 0.2)' }
};
